import React, { Suspense, useCallback, useEffect } from 'react';

import { defineMessages, useIntl } from 'react-intl-next';
import { fetchQuery, graphql, useRelayEnvironment } from 'react-relay';

import { cssMap } from '@atlaskit/css';
import type { EmojiProvider } from '@atlaskit/emoji';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import {
	type AIEventsInstrumentationConfig,
	AIEventsInstrumentationProvider,
	useAIEventsInstrumentation,
} from '@atlassian/ai-analytics';
import { usePlatformInstrumentation } from '@atlassian/platform-analytics';
import { AtlassianRelayEnvironmentProvider } from '@atlassian/relay-environment-provider';

import { AudioFormat } from '../../../api/cc-smarts/getAudio';
import { AUDIO_CONTAINER_MODES } from '../../../types/audioContainerMode';
import type { AudioFeature } from '../../../types/audioFeature';
import { AudioPlayerStreamingProvider } from '../../context/AudioPlayerStreamingProvider';
import { EmojiProviderContext } from '../../context/EmojiProviderContext';
import { type FlagsContainer, FlagsProviderContext } from '../../context/FlagsProviderContext';
import { getAIFeatureName } from '../../effects/getAIFeatureName';
import { useAudioPlaybackSync } from '../../hooks/useAudioPlaybackSync';
import { useAudioPlayerStore } from '../../hooks/useAudioPlayerStore';
import { useAudioPlayerStreaming } from '../../hooks/useAudioPlayerStreaming';
import { useAudioPlayerStreamingStore } from '../../hooks/useAudioPlayerStreamingStore';
import { useMouseOverWithDelayedExit } from '../../hooks/useMouseOverWithDelayedExit';
import { AudioStreamingState } from '../../state/AudioPlayerStreamingStore';

import type { AudioContainerQuery } from './__generated__/AudioContainerQuery.graphql';
import { AudioBriefingChime } from './AudioBriefingChime';
import { AutoPlayManager } from './AutoPlayManager';
import { ExpandedAudioPlayer } from './ExpandedAudioPlayer';
import { MinifiedAudioPlayer } from './MinifiedAudioPlayer';
import { useAudioEscapeShortcut } from './useAudioEscapeShortcut';

const i18n = defineMessages({
	errorLoadingAudioPreferences: {
		id: 'ai-audio.audio-container.error-loading-audio-preferences',
		defaultMessage: 'Playback speed error',
		description: 'Error message shown when audio preferences fail to load',
	},
	errorDescription: {
		id: 'ai-audio.audio-container.error-loading-audio-preferences-description',
		defaultMessage: 'Please try again later',
		description: 'Error description shown when audio preferences fail to load',
	},
});

const styles = cssMap({
	container: {
		borderRadius: token('radius.xlarge'),
		position: 'relative',
		display: 'inline-block',
		verticalAlign: 'top',
		overflow: 'hidden',
	},
});

type AudioContainerProps = {
	product: string;
	cloudId: string;
	activationId: string;
	audioFeatures: AudioFeature[];
	emojiProvider: EmojiProvider;
	flags: FlagsContainer;
	pinToExpandedView?: boolean;
	closeFn?: () => void;
	userId?: string;
	displayName?: string;
	chimeAudioSrc?: string;
};

const isFocusInsideContainer = () => {
	const containerElement = document.querySelector('[data-testId="audio-container"]');

	if (!containerElement) {
		return false;
	}
	// document.activeElement is the currently focused element in the entire document
	const focusedElement = document.activeElement;

	// Check if the container itself or any of its descendants has focus
	const isInside = containerElement.contains(focusedElement);

	// Return the result
	return isInside;
};

const AudioContainerComponent = ({
	product,
	cloudId,
	activationId,
	audioFeatures,
	emojiProvider,
	flags,
	pinToExpandedView = false,
	closeFn,
	userId,
	displayName,
	chimeAudioSrc,
}: AudioContainerProps): React.JSX.Element => {
	const {
		activeAudioContainerMode,
		activeContent,
		activeMenu,
		isPlaying,
		pendingContent,
		playImmediately,
		resetPlayImmediately,
		setCustomization,
		setActiveAudioContainerMode,
		updatePlayerState,
		isHidden,
	} = useAudioPlayerStore();
	const { formatMessage } = useIntl();
	const { sendScreenEvent } = usePlatformInstrumentation();
	const { state, updateAudioPlayerStreamingStore } = useAudioPlayerStreamingStore();
	const isLoading = state.state === AudioStreamingState.LOADING;

	// Handle blur event when an element inside the container loses focus
	const handleBlur = () => {
		handleMouseLeave();
	};

	// Handle focus event when an element inside the container receives focus
	const handleFocus = () => {
		handleMouseEnter();
	};

	const handleMouseOverChange = useCallback(
		(isHovered: boolean) => {
			if (!isPlaying && activeAudioContainerMode === AUDIO_CONTAINER_MODES.EXPANDED) {
				return;
			}
			setActiveAudioContainerMode(
				// prevents minification if the popup is hovered or focused or if it's been hidden
				isHovered || isHidden || isFocusInsideContainer() ? AUDIO_CONTAINER_MODES.EXPANDED : AUDIO_CONTAINER_MODES.MINIFIED,
			);
		},
		[setActiveAudioContainerMode, isPlaying, activeAudioContainerMode, isHidden],
	);

	const { handleMouseEnter, handleMouseLeave, handleMouseEnterExcluded, handleMouseLeaveExcluded } =
		useMouseOverWithDelayedExit({
			mouseLeaveDelay: 3000,
			mouseEnterDelay: 500,
			initiallyHovered: true,
			resetKey: pendingContent?.contentId,
			isPinned: pinToExpandedView || !!activeMenu,
			onMouseOverChange: handleMouseOverChange,
		});

	useEffect(() => {
		updatePlayerState({
			product,
			cloudId,
			activationId,
		});
	}, [product, cloudId, activationId, updatePlayerState]);

	const { audioRef, fetchAudio, disposeStreamingResources, isStreamComplete } =
		useAudioPlayerStreaming({
			locale: 'en-US',
			format: AudioFormat.MP3,
			product,
			cloudId,
			activationId,
		});

	const handleClose = useCallback(() => {
		if (closeFn && isFocusInsideContainer()) {
			closeFn();
		}
		if (isLoading) {
			updateAudioPlayerStreamingStore({ type: AudioStreamingState.UNINITIALIZED });
			disposeStreamingResources();
		}
	}, [closeFn, isLoading, updateAudioPlayerStreamingStore, disposeStreamingResources]);

	//links escape key to close the dialog, but only when the pop-up has focus
	useAudioEscapeShortcut(handleClose);

	const { trackAIResultAction } = useAIEventsInstrumentation();

	const handleBuffer = useCallback(() => {
		trackAIResultAction('audioContentBuffer');
	}, [trackAIResultAction]);

	useEffect(() => {
		sendScreenEvent({
			eventType: 'screen',
			name: 'audioContainer',
		});
	}, [sendScreenEvent]);

	useEffect(() => {
		if (playImmediately) {
			fetchAudio();
			resetPlayImmediately();
		}
	}, [playImmediately, fetchAudio, resetPlayImmediately]);

	useAudioPlaybackSync({ audioRef, onBuffer: handleBuffer, flags });

	const environment = useRelayEnvironment();

	useEffect(() => {
		const fetchAudioPreference = async () => {
				const data = await fetchQuery<AudioContainerQuery>(
					environment,
					graphql`
						query AudioContainerQuery($cloudId: ID!) {
							confluence_getAudioPreference(cloudId: $cloudId) {
								tone
								length
								playbackSpeed
							}
						}
					`,
					{ cloudId },
				).toPromise();

				if (data) {
					const tone = data?.confluence_getAudioPreference?.tone;
					const length = data?.confluence_getAudioPreference?.length;
					const playbackSpeed = data?.confluence_getAudioPreference?.playbackSpeed;

					if (tone && length && playbackSpeed) {
						setCustomization({
							tone: tone,
							length: length,
							playbackSpeed: playbackSpeed,
						});
					} else {
						flags?.showFlag({
							type: 'error',
							title: formatMessage(i18n.errorLoadingAudioPreferences),
							description: formatMessage(i18n.errorDescription),
							close: 'auto',
						});
					}
				}
		};

		void fetchAudioPreference();
	}, [audioRef, environment, cloudId, setCustomization, flags, formatMessage]);

	useEffect(() => {
		if (isHidden && isStreamComplete) {
			disposeStreamingResources();
		}
	}, [isHidden, isStreamComplete, disposeStreamingResources]);

	return (
		<AudioPlayerStreamingProvider fetchAudio={fetchAudio} audioRef={audioRef}>
			{/* Enable auto-play of next track when current track ends */}
			{!isHidden && (
				<>
					<Suspense fallback={null}>
						<AutoPlayManager cloudId={cloudId} />
					</Suspense>
					<EmojiProviderContext.Provider value={{ emojiProvider }}>
						<FlagsProviderContext.Provider value={{ flags }}>
							<Box
								xcss={styles.container}
								style={{ width: `${activeAudioContainerMode.width}px` }}
								onMouseEnter={handleMouseEnter}
								onMouseLeave={handleMouseLeave}
								onFocus={handleFocus}
								onBlur={handleBlur}
								testId="audio-container"
								role="dialog"
								aria-modal="false"
								aria-labelledby="audio-title"
							>
								{activeAudioContainerMode === AUDIO_CONTAINER_MODES.EXPANDED ? (
									<ExpandedAudioPlayer
										audioFeatures={audioFeatures}
										userId={userId}
										displayName={displayName}
									/>
								) : (
									<MinifiedAudioPlayer
										title={activeContent.title || 'Untitled'}
										byline={activeContent.author || ''}
										onMouseEnterActionButton={handleMouseEnterExcluded}
										onMouseLeaveActionButton={handleMouseLeaveExcluded}
									/>
								)}
								{chimeAudioSrc && <AudioBriefingChime isLoading={isLoading} src={chimeAudioSrc} />}
							</Box>
						</FlagsProviderContext.Provider>
					</EmojiProviderContext.Provider>
				</>
			)}
			<audio ref={audioRef} />
		</AudioPlayerStreamingProvider>
	);
};

export const AudioContainer = (passThroughProps: AudioContainerProps): JSX.Element => {
	const { activeFeature, activeContent, hideAudioPlayer } = useAudioPlayerStore();

	const aiAnalyticsConfig: AIEventsInstrumentationConfig = {
		product: passThroughProps.product,
		subproduct: 'platform',
		aiFeatureName: getAIFeatureName(activeFeature),
		proactiveGeneratedAI: 0 as const,
		userGeneratedAI: 1 as const,
		enablePlatformEvents: true,
		skipAISessionValidation: true,
		additionalAttributes: {
			contentId: activeContent.contentId,
			contentType: activeContent.contentType,
		},
	};

	return (
		<AtlassianRelayEnvironmentProvider>
			<AIEventsInstrumentationProvider config={aiAnalyticsConfig}>
				<AudioContainerComponent {...passThroughProps} closeFn={hideAudioPlayer} />
			</AIEventsInstrumentationProvider>
		</AtlassianRelayEnvironmentProvider>
	);
};